import React, { useState, useEffect, useRef } from "react";

interface InputComponentProps {
  type: "text" | "password" | "textarea" | "select" | "date" | "choice";
  placeholder?: string;
  value: string | Date;
  onChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | Date
      | string
      | null
  ) => void;
  options?: string[];
  selected?: boolean;
  title?: string;
  name?: string;
  readOnly?: boolean;
}

function CustomDatePicker({
  value,
  onChange,
  placeholder,
}: {
  value: Date | null;
  onChange: (date: Date) => void;
  placeholder?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(value || new Date());
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentDate(
      new Date(parseInt(e.target.value), currentDate.getMonth(), 1)
    );
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), months.indexOf(e.target.value), 1)
    );
  };

  const handleDayClick = (day: number) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
      12
    );
    onChange(newDate);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        className="w-full p-2 text-medium border border-border rounded-md bg-componentBackground text-text appearance-none"
        value={value instanceof Date ? value.toLocaleDateString() : ""}
        onClick={() => setIsOpen(true)}
        placeholder={placeholder}
        readOnly
      />
      {isOpen && (
        <div
          ref={calendarRef}
          className="absolute top-full right-0 mt-1 bg-componentBackground border border-border rounded-md p-4 z-50 bg-white"
        >
          <div className="flex gap-2 mb-2">
            <select
              value={currentDate.getFullYear()}
              onChange={handleYearChange}
              className="bg-componentBackground border border-border rounded-sm"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <select
              value={months[currentDate.getMonth()]}
              onChange={handleMonthChange}
              className="bg-componentBackground border border-border rounded-sm"
            >
              {months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from(
              {
                length: getDaysInMonth(
                  currentDate.getFullYear(),
                  currentDate.getMonth()
                ),
              },
              (_, i) => i + 1
            ).map((day) => (
              <button
                key={day}
                onClick={() => handleDayClick(day)}
                className={`
                  border border-border rounded-sm
                  ${
                    value instanceof Date &&
                    value.getDate() === day &&
                    value.getMonth() === currentDate.getMonth() &&
                    value.getFullYear() === currentDate.getFullYear()
                      ? "bg-selected text-white"
                      : "bg-transparent text-text"
                  }
                  active:bg-selected active:text-white
                `}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function InputComponent({
  name,
  type,
  placeholder,
  value,
  onChange,
  selected = false,
  options,
  title,
  readOnly,
}: InputComponentProps) {
  const handleInteraction = (
    event: React.MouseEvent | React.ChangeEvent,
    newValue: any
  ) => {
    onChange(newValue);
  };

  return (
    <div className="w-full text-text">
      {title && (
        <label className="block text-small text-text mb-1">{title}</label>
      )}
      {type === "textarea" ? (
        <textarea
          className="w-full p-4 text-medium rounded-md bg-inputBackground text-text1 h-48 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          placeholder={placeholder}
          value={value as string}
          onChange={onChange}
        />
      ) : type === "select" ? (
        <select
          className="w-full p-2 text-medium  rounded-md bg-inputBackground text-text appearance-none focus:outline-none  focus:border focus:border-primary"
          value={value as string}
          onChange={(e) => handleInteraction(e, e.target.value)}
        >
          <option value="" disabled>
            {placeholder || "Select an option"}
          </option>
          {options?.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : type === "date" ? (
        <CustomDatePicker
          value={value instanceof Date ? value : null}
          onChange={onChange}
          placeholder={placeholder}
        />
      ) : type === "choice" ? (
        <div
          className={`
            px-5 py-2.5 rounded-full cursor-pointer text-xs font-medium
            inline-block text-center transition-colors duration-300 ease-in-out
          `}
          onClick={(e) => handleInteraction(e, value)}
        >
          {value as string}
        </div>
      ) : (
        <input
          name={name}
          type={type}
          className="w-full p-2 text-medium rounded-md bg-inputBackground text-text focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          placeholder={placeholder}
          value={value as string}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}
